/**
 * Доступ к датасету: страна по ISO3 и значение показателя по году.
 *
 * Ряды считаются отсортированными по году (так их пишет build.ts);
 * поиск ближайшего года — бинарный.
 */
import type { Indicator } from './indicators';
import { pointFlag, type CountryRecord, type Dataset, type Flag, type Point } from './schema';

/** Разрыв между запрошенным и ближайшим годом, после которого значение считается оценкой. */
export const MAX_FACT_GAP_YEARS = 5;

export interface Value {
  v: number;
  flag: Flag;
  year: number;
}

export function listCountries(ds: Dataset): CountryRecord[] {
  return ds.countries;
}

export function getCountry(ds: Dataset, iso3: string): CountryRecord {
  const rec = ds.countries.find((c) => c.iso3 === iso3);
  if (!rec) throw new Error(`Unknown country iso3: ${iso3}`);
  return rec;
}

/** Индекс точки с ближайшим годом (при равном расстоянии — более ранняя). */
function nearestIndex(series: readonly Point[], year: number): number {
  let lo = 0;
  let hi = series.length - 1;
  // Бинарный поиск первой точки с годом >= year.
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (series[mid]![0] < year) lo = mid + 1;
    else hi = mid;
  }
  // Кандидаты: series[lo] (первая >= year) и series[lo-1] (последняя < year).
  if (lo > 0) {
    const prev = series[lo - 1]!;
    const cur = series[lo]!;
    if (Math.abs(prev[0] - year) <= Math.abs(cur[0] - year)) return lo - 1;
  }
  return lo;
}

/**
 * Значение показателя страны в ближайшем к `year` году.
 * flag = 'estimate', если точка сама помечена оценкой или |Δгод| > MAX_FACT_GAP_YEARS.
 */
export function getValue(ds: Dataset, iso3: string, ind: Indicator, year: number): Value {
  const rec = getCountry(ds, iso3);
  const series = rec.series[ind];
  if (!series || series.length === 0) {
    throw new Error(`No data for indicator ${ind} in country ${iso3}`);
  }
  const point = series[nearestIndex(series, year)]!;
  const [pYear, v] = point;
  const flag: Flag =
    pointFlag(point) === 'estimate' || Math.abs(pYear - year) > MAX_FACT_GAP_YEARS ? 'estimate' : 'fact';
  return { v, flag, year: pYear };
}
